import { useEffect, useMemo, useState } from 'react'
import { fetchProducts, fetchCategories } from '../data/api'
import { SearchBar } from '../components/SearchBar'
import { FilterChips } from '../components/FilterChips'
import { ProductCard } from '../components/ProductCard'
import { SkeletonGrid } from '../components/SkeletonGrid'
import { EmptyState } from '../components/EmptyState'

export function CatalogView({ segment, preselectedCategory = null, onSelectProduct, onAdd }) {
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setProducts([])
    setSelectedCategory(null)

    Promise.all([fetchProducts(segment), fetchCategories(segment)])
      .then(([productsRes, categoriesRes]) => {
        if (cancelled) return
        if (productsRes.success) setProducts(productsRes.data.products)
        if (categoriesRes.success) setCategories(categoriesRes.data)
      })
      .catch((err) => {
        if (!cancelled) console.error('Catalog load failed:', err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [segment])

  useEffect(() => {
    if (preselectedCategory && categories.some((c) => c.name === preselectedCategory)) {
      setSelectedCategory(preselectedCategory)
    }
  }, [preselectedCategory, categories])

  const filteredProducts = useMemo(() => {
    let result = [...products]
    if (selectedCategory) {
      result = result.filter((p) => p.category === selectedCategory)
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q)
      )
    }
    return result
  }, [products, selectedCategory, search])

  return (
    <main className="px-4 pb-16 pt-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="font-display text-3xl font-bold text-brand-ink">
            {segment === 'retail' ? 'Retail Catalog' : 'Wholesale Catalog'}
          </h1>
          <SearchBar value={search} onChange={setSearch} onClear={() => setSearch('')} />
        </div>

        <FilterChips categories={categories} selected={selectedCategory} onSelect={setSelectedCategory} />

        {loading ? (
          <div className="mt-8">
            <SkeletonGrid count={8} />
          </div>
        ) : filteredProducts.length === 0 ? (
          <EmptyState
            context={search.trim() ? 'search' : 'default'}
            actionLabel="Clear filters"
            onAction={() => {
              setSearch('')
              setSelectedCategory(null)
            }}
          />
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 lg:gap-6">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                segment={segment}
                onSelect={onSelectProduct}
                onAdd={(p) => onAdd(p, segment === 'retail' ? 1 : p.moq || 1)}
              />
            ))}
          </div>
        )}
      </div>
    </main>
  )
}
